import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import AuthBlockedNotice from "@/components/auth/AuthBlockedNotice";
import { getBrandHost } from "@/lib/moduleHost";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
export default function RegisterPage() {
  const [form, setForm] = useState({ email: "", password: "", name: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [blockDetail, setBlockDetail] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Honour ?returnTo=… for deep-linked signups (Matchmaker quiz etc.).
  // Only in-app paths (starts with a single /) allowed.
  const rawReturnTo = params.get("returnTo");
  const returnTo = rawReturnTo && rawReturnTo.startsWith("/") &&
                   !rawReturnTo.startsWith("//") ? rawReturnTo : null;
  // Conversion-attribution params from /preview/:council/:suburb CTAs
  const refSource = params.get("ref") || "";
  const refCouncil = params.get("council") || "";
  const refSuburb  = params.get("suburb") || "";

  // Fire a single page-view analytics event on mount so we can see
  // which suburb-preview pages drive registration starts.
  useEffect(() => {
    if (!refSource && !refCouncil && !refSuburb) return;
    fetch(`${API}/api/analytics/conversion-event`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        event:   "register_page_view",
        ref:     refSource || null,
        council: refCouncil || null,
        suburb:  refSuburb || null,
      }),
    }).catch(() => {});
  }, [refSource, refCouncil, refSuburb]);

  function set(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    const res = await register(form);
    setSubmitting(false);
    if (res.ok) {
      setBlockDetail(null);
      const brand = getBrandHost()?.title || "HustleSuite";
      toast.success(`Welcome to ${brand}, ${form.name || "friend"}`);
      // Fire the conversion event so suburb-preview attribution shows
      // up in the funnel report.
      if (refSource || refCouncil || refSuburb) {
        fetch(`${API}/api/analytics/conversion-event`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            event:   "register_complete",
            ref:     refSource || null,
            council: refCouncil || null,
            suburb:  refSuburb || null,
          }),
        }).catch(() => {});
      }
      // Iter 197 — if this signup completes a group-dining invite,
      // auto-RSVP + redirect the newly-upgraded member back to the
      // group page. The backend has already fired the referral
      // credit to the host as part of upgrade_shadow_to_member.
      try {
        const pending = JSON.parse(
          sessionStorage.getItem("hp_pending_group_signup") || "null",
        );
        if (pending?.gid) {
          const q = [];
          if (pending.t) q.push(`t=${encodeURIComponent(pending.t)}`);
          if (pending.a) q.push(`a=${encodeURIComponent(pending.a)}`);
          const qs = q.length ? `?${q.join("&")}` : "";
          await fetch(
            `${API}/api/group-dining/${pending.gid}/rsvp${qs}`,
            {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                rsvp: "accepted",
                location_opt_in: true,
              }),
            },
          ).catch(() => {});
          sessionStorage.removeItem("hp_pending_group_signup");
          navigate(`/group-dining/${pending.gid}${qs}`, { replace: true });
          return;
        }
      } catch { /* silent */ }
      // If they came from a suburb-preview, drop them into their
      // newsletter prefs so they can set the council/suburb hint
      // straight away.
      navigate(
        returnTo ||
        (refSource === "suburb-preview" ? "/account/newsletter" : "/account"),
        { replace: true });
    } else if (res.blockDetail) {
      setBlockDetail(res.blockDetail);
    } else {
      setBlockDetail(null);
      toast.error(res.error);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] dark:bg-stone-950 px-4 py-12">
      {/* iter316 — NO local Back button, NO local LanguagePicker.
       *  The black top global bar (GoogleTranslateBar) already carries
       *  the "← Back to {Module}" chip + Language + Theme + Modules
       *  dropdown. Auth pages must not duplicate that chrome. */}
      <div
        data-testid="register-card"
        className="w-full max-w-md bg-white rounded-2xl border border-[#E8E5DF] p-8 md:p-10"
      >
        <Link
          to="/"
          data-testid="register-back-home"
          className="font-serif-display text-2xl text-[#121614] hover:text-[#C05746]"
        >
          {(getBrandHost()?.title) || "HustleSuite"}
        </Link>
        <h1 className="font-serif-display text-3xl md:text-4xl mt-6">Create an account</h1>
        <p className="text-sm text-[#5C625E] mt-2">
          Save your dietary preferences once and we&apos;ll filter the menu for you on every visit.
        </p>

        {refSource === "suburb-preview" && refCouncil && refSuburb && (
          <div data-testid="register-suburb-preview-chip"
                  className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-xs text-emerald-900">
            📍 You&apos;re signing up from the <strong>{refSuburb}</strong>{" "}
            preview. We&apos;ll pre-fill your newsletter prefs after signup.
          </div>
        )}

        {blockDetail ? (
          <div className="mt-6">
            <AuthBlockedNotice
              detail={blockDetail}
              onRetry={() => setBlockDetail(null)}
            />
          </div>
        ) : null}

        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          <div>
            <Label htmlFor="reg-name">Name</Label>
            <Input
              id="reg-name"
              data-testid="register-name-input"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              className="mt-2 rounded-xl h-11"
            />
          </div>
          <div>
            <Label htmlFor="reg-email">Email</Label>
            <Input
              id="reg-email"
              data-testid="register-email-input"
              type="email"
              required
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              className="mt-2 rounded-xl h-11"
            />
          </div>
          <div>
            <Label htmlFor="reg-phone">Phone (optional)</Label>
            <Input
              id="reg-phone"
              data-testid="register-phone-input"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              className="mt-2 rounded-xl h-11"
            />
          </div>
          <div>
            <Label htmlFor="reg-password">Password</Label>
            <Input
              id="reg-password"
              data-testid="register-password-input"
              type="password"
              required
              minLength={8}
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              className="mt-2 rounded-xl h-11"
            />
            <p className="text-[11px] text-[#5C625E] mt-1">Minimum 8 characters.</p>
          </div>
          <Button
            type="submit"
            data-testid="register-submit-button"
            disabled={submitting}
            className="w-full bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full h-12"
          >
            {submitting ? "Creating account…" : "Create account"}
          </Button>
        </form>

        <p className="mt-6 text-xs text-[#5C625E] text-center">
          Already have an account?{" "}
          <Link
            to="/admin/login"
            data-testid="register-login-link"
            className="text-[#C05746] hover:text-[#A64A3C] font-medium"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
